import { useState } from 'react';
import { Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Textarea,
} from '@shared/ui';
import { ApiError } from '@shared/api';
import {
  useChangeOrderStatusMutation,
  type OrderStatus,
  type OrderView,
} from '@entities/order';

interface StatusChangeDialogProps {
  order: OrderView;
  target: OrderStatus;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function StatusChangeDialog({ order, target, open, onOpenChange }: StatusChangeDialogProps) {
  const { t } = useTranslation();
  const mutation = useChangeOrderStatusMutation(order.id);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const isCancel = target === 'CANCELLED';

  const handleConfirm = () => {
    setError(null);
    mutation.mutate(
      { status: target, note: note.trim() || null },
      {
        onSuccess: () => {
          setNote('');
          onOpenChange(false);
        },
        onError: (err) =>
          setError(err instanceof ApiError ? err.message : 'Status change failed'),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isCancel
              ? t('orders.statusDialog.cancelTitle')
              : t('orders.statusDialog.moveTitle', { status: t(`orders.status.${target}`) })}
          </DialogTitle>
          <DialogDescription>
            {isCancel
              ? t('orders.statusDialog.cancelDescription')
              : t('orders.statusDialog.moveDescription')}
          </DialogDescription>
        </DialogHeader>

        <FormField label={t('orders.statusDialog.noteLabel')} hint={t('orders.statusDialog.noteHint')}>
          {(p) => (
            <Textarea
              {...p}
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('orders.statusDialog.notePlaceholder')}
            />
          )}
        </FormField>

        {error ? <Alert type="error" showIcon message={error} /> : null}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            variant={isCancel ? 'destructive' : 'default'}
            loading={mutation.isPending}
            onClick={handleConfirm}
          >
            {isCancel ? t('orders.detail.cancelOrder') : t('orders.statusDialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
